/**
 * High-level auth flows — these orchestrate libsodium key derivation, the API
 * client, and the in-memory session state. The crypto module is pure; this
 * module is where the *workflow* (signup, login, password change, recovery)
 * lives.
 */
import { api } from './api';
import { setSession, clearSession as clearLocal } from './session.svelte';
import { privateTagIndex } from './tagIndex';
import {
  ready,
  generateDek,
  generateSalt,
  generateRecoveryCode,
  normalizeRecoveryCode,
  deriveKey,
  deriveAuthVerifier,
  wrapDek,
  unwrapDek,
  bytesToBase64,
  base64ToBytes,
  zero,
} from './crypto';
import type { MeResponse } from '../../../shared/types';

export interface SignupResult {
  user: MeResponse;
  recoveryCode: string; // shown once, never sent
}

/**
 * Signup: generate DEK and both wraps client-side, send wraps + auth verifier
 * to the server.
 */
export async function signup(
  email: string,
  password: string,
  inviteToken?: string,
): Promise<SignupResult> {
  await ready();

  const dek = generateDek();
  const recoveryCode = generateRecoveryCode();
  const normalisedCode = normalizeRecoveryCode(recoveryCode);

  const kekSalt = generateSalt();
  const recSalt = generateSalt();
  const authSalt = generateSalt();
  // Fourth salt: for the recovery verifier. Must differ from rec_salt so
  // learning the verifier hash doesn't give the attacker KEK_rec material.
  const recAuthSalt = generateSalt();

  const kekPw = deriveKey(password, kekSalt);
  const kekRec = deriveKey(normalisedCode, recSalt);
  const authVerifier = deriveAuthVerifier(password, authSalt);
  const recAuthVerifier = deriveAuthVerifier(normalisedCode, recAuthSalt);

  const wrappedPw = wrapDek(dek, kekPw);
  const wrappedRec = wrapDek(dek, kekRec);

  // Zero out KEKs we no longer need before going async.
  zero(kekPw);
  zero(kekRec);

  const user = await api.signup({
    email,
    auth_salt: bytesToBase64(authSalt),
    auth_verifier: authVerifier,
    kek_salt: bytesToBase64(kekSalt),
    wrapped_dek_pw: bytesToBase64(wrappedPw.ciphertext),
    dek_pw_nonce: bytesToBase64(wrappedPw.nonce),
    rec_salt: bytesToBase64(recSalt),
    wrapped_dek_rec: bytesToBase64(wrappedRec.ciphertext),
    dek_rec_nonce: bytesToBase64(wrappedRec.nonce),
    rec_auth_salt: bytesToBase64(recAuthSalt),
    rec_auth_verifier: recAuthVerifier,
    ...(inviteToken ? { invite_token: inviteToken } : {}),
  });

  setSession(user, dek);
  return { user, recoveryCode };
}

/**
 * Login: fetch challenge, derive both KEK and verifier, send verifier to login,
 * unwrap DEK locally on success.
 */
export async function login(email: string, password: string): Promise<MeResponse> {
  await ready();

  const challenge = await api.challenge(email);
  const authSalt = base64ToBytes(challenge.auth_salt);
  const kekSalt = base64ToBytes(challenge.kek_salt);

  const authVerifier = deriveAuthVerifier(password, authSalt);
  const kekPw = deriveKey(password, kekSalt);

  const user = await api.login({ email, auth_verifier: authVerifier });

  const dek = unwrapDek(
    {
      ciphertext: base64ToBytes(challenge.wrapped_dek_pw),
      nonce: base64ToBytes(challenge.dek_pw_nonce),
    },
    kekPw,
  );
  zero(kekPw);

  setSession(user, dek);
  return user;
}

export async function logout(): Promise<void> {
  await api.logout().catch(() => null);
  await privateTagIndex.clear().catch(() => null);
  clearLocal();
}

/**
 * Password change: unwrap DEK with old password, re-wrap with new password,
 * push the new material. Recovery wrap stays put.
 */
export async function changePassword(oldPassword: string, newPassword: string, email: string): Promise<void> {
  await ready();

  // Re-derive old keys from the server's current material.
  const challenge = await api.challenge(email);
  const oldKek = deriveKey(oldPassword, base64ToBytes(challenge.kek_salt));
  const dek = unwrapDek(
    {
      ciphertext: base64ToBytes(challenge.wrapped_dek_pw),
      nonce: base64ToBytes(challenge.dek_pw_nonce),
    },
    oldKek,
  );
  zero(oldKek);

  const kekSalt = generateSalt();
  const authSalt = generateSalt();
  const newKek = deriveKey(newPassword, kekSalt);
  const verifier = deriveAuthVerifier(newPassword, authSalt);
  const wrapped = wrapDek(dek, newKek);
  zero(newKek);

  await api.passwordChange({
    auth_salt: bytesToBase64(authSalt),
    auth_verifier: verifier,
    kek_salt: bytesToBase64(kekSalt),
    wrapped_dek_pw: bytesToBase64(wrapped.ciphertext),
    dek_pw_nonce: bytesToBase64(wrapped.nonce),
  });
}

/**
 * Recovery: user provides their recovery code; we unwrap the DEK via the
 * recovery wrap, then build new password-side material.
 */
export async function recover(
  email: string,
  recoveryCode: string,
  newPassword: string,
): Promise<void> {
  await ready();

  const challenge = await api.recoveryChallenge(email);
  const normalisedCode = normalizeRecoveryCode(recoveryCode);

  const kekRec = deriveKey(normalisedCode, base64ToBytes(challenge.rec_salt));
  const dek = unwrapDek(
    {
      ciphertext: base64ToBytes(challenge.wrapped_dek_rec),
      nonce: base64ToBytes(challenge.dek_rec_nonce),
    },
    kekRec,
  );
  zero(kekRec);

  // Derive the recovery verifier the server will check before doing anything.
  const recAuthVerifier = deriveAuthVerifier(
    normalisedCode,
    base64ToBytes(challenge.rec_auth_salt),
  );

  const kekSalt = generateSalt();
  const authSalt = generateSalt();
  const newKek = deriveKey(newPassword, kekSalt);
  const verifier = deriveAuthVerifier(newPassword, authSalt);
  const wrapped = wrapDek(dek, newKek);
  zero(newKek);
  // dek not yet zeroed — we still need it to log the user back in.

  await api.recoveryComplete({
    email,
    rec_auth_verifier: recAuthVerifier,
    auth_salt: bytesToBase64(authSalt),
    auth_verifier: verifier,
    kek_salt: bytesToBase64(kekSalt),
    wrapped_dek_pw: bytesToBase64(wrapped.ciphertext),
    dek_pw_nonce: bytesToBase64(wrapped.nonce),
  });

  // Log in with the new password so the user lands in an authenticated state.
  await login(email, newPassword);
}
